import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembershipPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Details
    surname: '',
    first_name: '',
    second_name: '',
    postal_address: '',
    phone: '',
    fax: '',
    email: '',
    birth_date: '',
    gender: '',
    marital_status: '',
    num_dependents: 0,
    education_level: '',
    accommodation: '',
    
    // Employment Details
    department: '',
    division: '',
    district: '',
    province: '',
    employee_id: '',
    position: '',
    organization: '',
    fortnightly_gross_salary: '',
    job_grade: '',
    salary_point: '',
    employment_date: '',
    years_of_service: '',
    employment_type: '',
    
    // Union Contribution
    authorize_deduction: false,
    deduction_amount: '',
    salary_calculator_name: '',
    salary_calculator_address: '',
    
    // Dependents
    dependents: []
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDependentChange = (index, field, value) => {
    const newDependents = [...formData.dependents];
    newDependents[index] = { ...newDependents[index], [field]: value };
    setFormData(prev => ({ ...prev, dependents: newDependents }));
  };

  const addDependent = () => {
    setFormData(prev => ({
      ...prev,
      dependents: [...prev.dependents, { name: '', date_of_birth: '', has_income: false, employer: '' }]
    }));
  };

  const removeDependent = (index) => {
    setFormData(prev => ({
      ...prev,
      dependents: prev.dependents.filter((_, i) => i !== index)
    }));
  };

  // Calculate deduction when salary changes
  useEffect(() => {
    if (formData.fortnightly_gross_salary && formData.authorize_deduction) {
      const salary = parseFloat(formData.fortnightly_gross_salary);
      if (!isNaN(salary)) {
        const deduction = (salary * 0.015).toFixed(2);
        setFormData(prev => ({ ...prev, deduction_amount: deduction }));
      }
    }
  }, [formData.fortnightly_gross_salary, formData.authorize_deduction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        fortnightly_gross_salary: formData.fortnightly_gross_salary ? parseFloat(formData.fortnightly_gross_salary) : null,
        num_dependents: formData.num_dependents ? parseInt(formData.num_dependents) : null,
        years_of_service: formData.years_of_service ? parseInt(formData.years_of_service) : null,
        deduction_amount: formData.deduction_amount ? parseFloat(formData.deduction_amount) : null,
      };

      await axios.post(`${API}/members`, submitData);
      toast.success('Membership application submitted successfully!', {
        description: 'We will review your application and get back to you soon.'
      });
      
      // Reset form
      setFormData({
        surname: '', first_name: '', second_name: '', postal_address: '', phone: '', fax: '',
        email: '', birth_date: '', gender: '', marital_status: '', num_dependents: 0,
        education_level: '', accommodation: '', department: '', division: '', district: '',
        province: '', employee_id: '', position: '', organization: '', fortnightly_gross_salary: '',
        job_grade: '', salary_point: '', employment_date: '', years_of_service: '',
        employment_type: '', authorize_deduction: false, deduction_amount: '',
        salary_calculator_name: '', salary_calculator_address: '', dependents: []
      });
      setCurrentStep(1);
    } catch (error) {
      console.error('Error submitting membership:', error);
      toast.error('Failed to submit application', {
        description: 'Please try again later or contact us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Personal Details' },
    { id: 2, title: 'Employment Details' },
    { id: 3, title: 'Union Contribution' },
    { id: 4, title: 'Dependents' },
    { id: 5, title: 'Review & Submit' }
  ];

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.surname && formData.first_name && formData.phone && formData.email;
    }
    if (currentStep === 2) {
      return formData.department && formData.position && formData.organization;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 to-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Membership Application
          </h1>
          <p className="text-orange-100 max-w-2xl mx-auto">
            Join the Public Employees Association of Papua New Guinea
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                      ${currentStep >= step.id 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-200 text-gray-500'}`}
                  >
                    {currentStep > step.id ? <CheckCircle2 size={20} /> : step.id}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${currentStep >= step.id ? 'text-orange-600 font-semibold' : 'text-gray-400'}`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step.id ? 'bg-orange-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} data-testid="membership-form">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              
              {/* Step 1: Personal Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Personal Details</h2>
                    <p className="text-sm text-gray-500 mt-1">Please print your name exactly as it appears on your pay slip</p>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="surname">Surname *</Label>
                      <Input id="surname" name="surname" value={formData.surname} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="second_name">Second Name</Label>
                      <Input id="second_name" name="second_name" value={formData.second_name} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="postal_address">Postal Address</Label>
                    <Input id="postal_address" name="postal_address" value={formData.postal_address} onChange={handleChange} className="mt-1" />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="phone">Telephone No. *</Label>
                      <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="fax">Facsimile No.</Label>
                      <Input id="fax" name="fax" value={formData.fax} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="email">E-Mail Address *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="birth_date">Birth Date</Label>
                      <Input id="birth_date" name="birth_date" type="date" value={formData.birth_date} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      <select id="gender" name="gender" value={formData.gender} onChange={handleChange} className="mt-1 w-full h-10 px-3 border rounded-md">
                        <option value="">Select</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="marital_status">Marital Status</Label>
                      <select id="marital_status" name="marital_status" value={formData.marital_status} onChange={handleChange} className="mt-1 w-full h-10 px-3 border rounded-md">
                        <option value="">Select</option>
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="num_dependents">Dependents (Below 22 yrs)</Label>
                      <Input id="num_dependents" name="num_dependents" type="number" min="0" value={formData.num_dependents} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="education_level">Educational Level</Label>
                      <select id="education_level" name="education_level" value={formData.education_level} onChange={handleChange} className="mt-1 w-full h-10 px-3 border rounded-md">
                        <option value="">Select</option>
                        <option value="Primary">Primary</option>
                        <option value="Secondary">Secondary</option>
                        <option value="Certificate">Certificate</option>
                        <option value="Diploma">Diploma</option>
                        <option value="Bachelor">Bachelor's Degree</option>
                        <option value="Master">Master's Degree</option>
                        <option value="PhD">PhD</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="accommodation">Accommodation</Label>
                      <select id="accommodation" name="accommodation" value={formData.accommodation} onChange={handleChange} className="mt-1 w-full h-10 px-3 border rounded-md">
                        <option value="">Select</option>
                        <option value="Rented">Rented</option>
                        <option value="Own">Own</option>
                        <option value="Institutional">Institutional</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Employment Details */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Employment Details</h2>
                    <p className="text-sm text-gray-500 mt-1">Your current employment information</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="department">Department *</Label>
                      <Input id="department" name="department" value={formData.department} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="division">Division</Label>
                      <Input id="division" name="division" value={formData.division} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="district">District</Label>
                      <Input id="district" name="district" value={formData.district} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="province">Province</Label>
                      <Input id="province" name="province" value={formData.province} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="employee_id">Employee No.</Label>
                      <Input id="employee_id" name="employee_id" value={formData.employee_id} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="position">Designation *</Label>
                      <Input id="position" name="position" value={formData.position} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="organization">Organization *</Label>
                    <Input id="organization" name="organization" value={formData.organization} onChange={handleChange} required className="mt-1" />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="fortnightly_gross_salary">Fortnightly Gross Salary (K)</Label>
                      <Input id="fortnightly_gross_salary" name="fortnightly_gross_salary" type="number" step="0.01" value={formData.fortnightly_gross_salary} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="job_grade">Job Grade</Label>
                      <Input id="job_grade" name="job_grade" value={formData.job_grade} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="salary_point">Salary Point (e.g. 7.1, 8A)</Label>
                      <Input id="salary_point" name="salary_point" value={formData.salary_point} onChange={handleChange} className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="employment_date">Employment Date</Label>
                      <Input id="employment_date" name="employment_date" type="date" value={formData.employment_date} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="years_of_service">Years of Service</Label>
                      <Input id="years_of_service" name="years_of_service" type="number" min="0" value={formData.years_of_service} onChange={handleChange} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="employment_type">Employment Type</Label>
                      <select id="employment_type" name="employment_type" value={formData.employment_type} onChange={handleChange} className="mt-1 w-full h-10 px-3 border rounded-md">
                        <option value="">Select</option>
                        <option value="Permanent">Permanent</option>
                        <option value="Temporary">Temporary</option>
                        <option value="Casual">Casual</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Union Contribution */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Union Contribution Authorization</h2>
                    <p className="text-sm text-gray-500 mt-1">Industrial Organisation Act 1962 - ACTS SECT. 63A</p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-4">NOTICE TO DEDUCT UNION CONTRIBUTIONS</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="salary_calculator_name">Name of Salary Section</Label>
                        <Input id="salary_calculator_name" name="salary_calculator_name" placeholder="TO: THE SALARY CALCULATOR" value={formData.salary_calculator_name} onChange={handleChange} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="salary_calculator_address">Address of Salary Section</Label>
                        <Input id="salary_calculator_address" name="salary_calculator_address" value={formData.salary_calculator_address} onChange={handleChange} className="mt-1" />
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-700 mb-4">
                        You are required to deduct <strong>ONE & HALF PERCENT (1.5%)</strong> from my Base/Gross Salary each Fortnight 
                        and pay the amount so deducted to Public Employees Association of Papua New Guinea until you receive contrary 
                        instructions from me through the Public Employees Association.
                      </p>
                      
                      {formData.fortnightly_gross_salary && (
                        <div className="bg-blue-50 p-3 rounded-lg mb-4">
                          <p className="text-sm text-blue-800">
                            Based on your gross salary of <strong>K{parseFloat(formData.fortnightly_gross_salary).toFixed(2)}</strong>, 
                            the 1.5% deduction will be <strong>K{(parseFloat(formData.fortnightly_gross_salary) * 0.015).toFixed(2)}</strong> per fortnight.
                          </p>
                        </div>
                      )}

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="authorize_deduction" 
                          checked={formData.authorize_deduction} 
                          onChange={handleChange}
                          className="mt-1 w-5 h-5 text-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          I authorize the deduction of 1.5% from my gross salary each fortnight. I understand that this deduction 
                          will increase each time I receive an increase in my basic salary.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Dependents */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Dependents Details</h2>
                    <p className="text-sm text-gray-500 mt-1">Note: Dependents must be below the age of 22 apart from your spouse</p>
                  </div>

                  {formData.dependents.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                      <p className="text-gray-500 mb-4">No dependents added yet</p>
                      <Button type="button" onClick={addDependent} variant="outline" className="gap-2">
                        <Plus size={18} /> Add Dependent
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.dependents.map((dep, idx) => (
                        <div key={idx} className="border rounded-xl p-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-semibold text-gray-900">Dependent {idx + 1}</h4>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeDependent(idx)} className="text-red-500 hover:text-red-600">
                              <Trash2 size={18} />
                            </Button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label>Name</Label>
                              <Input value={dep.name} onChange={(e) => handleDependentChange(idx, 'name', e.target.value)} className="mt-1" />
                            </div>
                            <div>
                              <Label>Date of Birth</Label>
                              <Input type="date" value={dep.date_of_birth} onChange={(e) => handleDependentChange(idx, 'date_of_birth', e.target.value)} className="mt-1" />
                            </div>
                          </div>
                          <div className="mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={dep.has_income} 
                                onChange={(e) => handleDependentChange(idx, 'has_income', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">This dependent has other sources of income</span>
                            </label>
                            {dep.has_income && (
                              <div className="mt-2">
                                <Label>Name of Employer</Label>
                                <Input value={dep.employer} onChange={(e) => handleDependentChange(idx, 'employer', e.target.value)} className="mt-1" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button type="button" onClick={addDependent} variant="outline" className="w-full gap-2">
                        <Plus size={18} /> Add Another Dependent
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Review & Submit */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="border-b pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Review Your Application</h2>
                    <p className="text-sm text-gray-500 mt-1">Please review your information before submitting</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Personal Details Summary */}
                    <div className="bg-orange-50 rounded-xl p-4">
                      <h3 className="font-bold text-orange-800 mb-3">Personal Details</h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Name:</span> {formData.surname} {formData.first_name} {formData.second_name}</p>
                        <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                        <p><span className="text-gray-500">Phone:</span> {formData.phone}</p>
                        <p><span className="text-gray-500">Gender:</span> {formData.gender || 'N/A'}</p>
                        <p><span className="text-gray-500">Marital Status:</span> {formData.marital_status || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Employment Details Summary */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h3 className="font-bold text-blue-800 mb-3">Employment Details</h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Organization:</span> {formData.organization}</p>
                        <p><span className="text-gray-500">Department:</span> {formData.department}</p>
                        <p><span className="text-gray-500">Position:</span> {formData.position}</p>
                        <p><span className="text-gray-500">Employee No:</span> {formData.employee_id || 'N/A'}</p>
                        <p><span className="text-gray-500">Gross Salary:</span> {formData.fortnightly_gross_salary ? `K${formData.fortnightly_gross_salary}` : 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Union Contribution Summary */}
                  <div className={`rounded-xl p-4 ${formData.authorize_deduction ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <h3 className={`font-bold mb-2 ${formData.authorize_deduction ? 'text-green-800' : 'text-gray-600'}`}>
                      Union Contribution: {formData.authorize_deduction ? 'Authorized' : 'Not Authorized'}
                    </h3>
                    {formData.authorize_deduction && formData.deduction_amount && (
                      <p className="text-sm text-green-700">Deduction amount: K{formData.deduction_amount} per fortnight (1.5%)</p>
                    )}
                  </div>

                  {/* Dependents Summary */}
                  {formData.dependents.length > 0 && (
                    <div className="bg-purple-50 rounded-xl p-4">
                      <h3 className="font-bold text-purple-800 mb-3">Dependents ({formData.dependents.length})</h3>
                      <div className="space-y-2">
                        {formData.dependents.map((dep, idx) => (
                          <p key={idx} className="text-sm">{dep.name} {dep.date_of_birth && `(DOB: ${dep.date_of_birth})`}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <p className="text-sm text-yellow-800">
                      By submitting this application, I confirm that all information provided is accurate and complete. 
                      I understand that my application will be reviewed by PEA PNG.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={currentStep === 1}
                  className="gap-2"
                >
                  <ChevronLeft size={18} /> Previous
                </Button>

                {currentStep < 5 ? (
                  <Button
                    type="button"
                    onClick={() => setCurrentStep(prev => prev + 1)}
                    disabled={!canProceed()}
                    className="gap-2 bg-orange-500 hover:bg-orange-600"
                  >
                    Next <ChevronRight size={18} />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="gap-2 bg-green-500 hover:bg-green-600"
                  >
                    {loading ? 'Submitting...' : (
                      <>
                        <UserPlus size={18} /> Submit Application
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MembershipPage;
